import { BaseResponse } from 'domain/payload/response/BaseResponseDto';
import axiosInstance from '../config/AxiosConfig'
import { ProjectListResp } from 'domain/payload/response/project/ProjectListResp';
import { ProjectConfigResp } from 'domain/payload/response/project/ProjectConfigResp';
import { ProjectDetailResp } from 'domain/payload/response/project/ProjectDetailResp';
import { ProjectCreateReq } from 'domain/payload/request/project/ProjectCreateReq';
import { ProjectUpdateReq } from 'domain/payload/request/project/ProjectUpdateReq';
import { ProjectTaskListResp } from 'domain/payload/response/project/ProjectTaskListResp';
import { ProjectTaskConfigResp, ProjectTaskConfigJobResp, ProjectTaskConfigFieldUserResp } from 'domain/payload/response/project/ProjectTaskConfigResp';
import { ProjectTaskAssignReq } from 'domain/payload/request/project/ProjectTaskAssignReq';
import { ProjectJobListResp } from 'domain/payload/response/project/ProjectJobListResp';
import { ProjectTaskAssignByJobReq } from 'domain/payload/request/project/ProjectTaskAssignByJobReq';
import { ProjectTaskProceedJobAssignmentReq } from 'domain/payload/request/project/ProjectTaskProceedJobAssignmentReq';
import { ProjectJobGeoListResp } from 'domain/payload/response/project/ProjectJobGeoListResp';
import { ProjectJobGeoConfigResp } from 'domain/payload/response/project/ProjectJobGeoConfigResp';
import { ProjectJobDetailResp } from 'domain/payload/response/project/ProjectJobDetailResp';
import { ProjectJobUpdateReq } from 'domain/payload/request/project/ProjectJobUpdateReq';
import { ProjectJobUpdateResp } from 'domain/payload/response/project/ProjectJobUpdateResp';
import { JobCreationInquiryPayload } from 'domain/payload/request/project/ProjectJobCreationReq';
import { JobCreationConfigData, JobCreationInquiryData, JobCreationExecuteData } from 'domain/payload/response/project/ProjectJobCreationResp';
import { ProjectClusterConfigResp } from 'domain/payload/response/project/ProjectClusterConfigResp';
import { ProjectJobChartResp } from 'domain/payload/response/project/ProjectJobChartResp';

const ProjectService = () => {
  const projectList = async (
    token: string,
    search: string | null,
    page: number | null,
    size: number | null,
    isRunning?: boolean | null
  ): Promise<BaseResponse<ProjectListResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          search: search,
          page: page,
          size: size,
          ...(isRunning !== null && isRunning !== undefined && { isRunning })
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectDetail = async (
    token: string,
    id: string
  ): Promise<BaseResponse<ProjectDetailResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/detail?id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectConfig = async (
    token: string
  ): Promise<BaseResponse<ProjectConfigResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/config`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectCreate = async (
    token: string,
    request: ProjectCreateReq
  ): Promise<BaseResponse<ProjectDetailResp>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/project/create`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectUpdate = async (
    token: string,
    request: ProjectUpdateReq
  ): Promise<BaseResponse<ProjectDetailResp>> => {
    try {
      const response = await axiosInstance.put(`/v1/legacy/cms/project/update`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectDelete = async (
    token: string,
    id: string
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.delete(`/v1/legacy/cms/project/delete?id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectTaskList = async (
    token: string,
    projectId: string | null,
    jobId: string | null,
    search: string | null,
    page: number | null,
    size: number | null
  ): Promise<BaseResponse<ProjectTaskListResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/task/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId,
          jobId: jobId,
          search: search,
          page: page,
          size: size
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectJobList = async (
    token: string,
    projectId: string,
    statusJob: string,
    search: string | null,
    page: number | null,
    size: number | null,
    formId?: string | null,
    tribeId?: string | null,
    groupName?: string | null
  ): Promise<BaseResponse<ProjectJobListResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/job/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId,
          status: statusJob,
          search: search,
          page: page,
          size: size,
          ...(formId ? { formId } : {}),
          ...(tribeId ? { tribeId } : {}),
          ...(groupName ? { groupName } : {})
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectJobDetail = async (
    token: string,
    id: string
  ): Promise<BaseResponse<ProjectJobDetailResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/job/detail?id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectJobGeoList = async (
    token: string,
    projectId: string,
    statusJob: string,
    search: string | null,
    formId?: string | null,
    tribeId?: string | null,
    groupName?: string | null
  ): Promise<BaseResponse<ProjectJobGeoListResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/job/geo/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId,
          status: statusJob,
          search: search,
          ...(formId ? { formId } : {}),
          ...(tribeId ? { tribeId } : {}),
          ...(groupName ? { groupName } : {})
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectJobGroupConfig = async (
    token: string,
    projectId: string
  ): Promise<BaseResponse<string[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/job/group-config`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const projectJobChart = async (
    token: string,
    projectId: string,
    search?: string | null,
    formId?: string | null,
    tribeId?: string | null,
    groupName?: string | null,
    status?: string | null
  ): Promise<BaseResponse<ProjectJobChartResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/job/chart`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId,
          ...(search ? { search } : {}),
          ...(formId ? { formId } : {}),
          ...(tribeId ? { tribeId } : {}),
          ...(groupName ? { groupName } : {}),
          ...(status ? { status } : {})
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const projectJobGeoConfig = async (
    token: string,
  ): Promise<BaseResponse<ProjectJobGeoConfigResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/job/geo/config`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectTaskConfig = async (
    token: string,
    projectId: string
  ): Promise<BaseResponse<ProjectTaskConfigResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/task/config`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectTaskAssign = async (
    token: string,
    request: ProjectTaskAssignReq
  ): Promise<BaseResponse<ProjectDetailResp>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/project/task/assign`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectTaskAssignByJob = async (
    token: string,
    request: ProjectTaskAssignByJobReq
  ): Promise<BaseResponse<ProjectDetailResp>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/project/task/assign-by-job`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const downloadTemplateAdvanceAssignment = async (
    token: string
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.post(
        `/v1/legacy/cms/report-engine/download`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            reportCode: "DOWNLOAD_TEMPLATE_ADVANCE_ASSIGNMENT",
            extension: "xlsx",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const checkPreviewAssignment = async (
    token: string,
    projectId: string,
    request: {
      assignmentQueue: {
        jobIds?: string[];
        placeRefIds?: string[];
        userIds?: string[];
        userEmails?: string[];
        action: string;
      }[];
    }
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.post(
        `/v1/legacy/cms/project/task/check-preview-assignment`,
        {
          projectId: projectId,
          assignmentQueue: request.assignmentQueue
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const projectJobExportXlsx = async (
    token: string,
    search: string,
    projectId: string,
    statusJob: string
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.get(
        `/v1/legacy/cms/project/job/export-xlsx`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            search: search,
            projectId: projectId,
            status: statusJob
          },
        }
      );

      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const proceedJobAssignment = async (
    token: string,
    request: ProjectTaskProceedJobAssignmentReq
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.post(
        `/v1/legacy/cms/project/task/proceed-job-assignment`,
        request,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const projectTaskConfigJob = async (
    token: string,
    projectId: string,
    search: string | null,
    page: number | null,
    size: number | null
  ): Promise<BaseResponse<ProjectTaskConfigJobResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/task/config-job`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId,
          search: search,
          page: page,
          size: size
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  // API 2: GET /api/v1/legacy/cms/project/task/config-field-user
  const projectTaskConfigFieldUser = async (
    token: string,
    search: string | null,
    page: number | null,
    size: number | null
  ): Promise<BaseResponse<ProjectTaskConfigFieldUserResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/task/config-field-user`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          search: search,
          page: page,
          size: size
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const uploadLogo = async (
    token: string,
    projectId: string,
    file: File
  ): Promise<BaseResponse<any>> => {
    try {
      const formData = new FormData();
      formData.append('projectId', projectId);
      formData.append('file', file);

      const response = await axiosInstance.post(`/v1/legacy/cms/project/upload-logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const projectJobUpdate = async (
    token: string,
    request: ProjectJobUpdateReq
  ): Promise<BaseResponse<ProjectJobUpdateResp>> => {
    try {
      const response = await axiosInstance.put(`/v1/legacy/cms/project/job/update`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const getJobCreationConfig = async (
    token: string,
    projectId: string
  ): Promise<BaseResponse<JobCreationConfigData>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/job-creation/config`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const jobCreationInquiry = async (
    token: string,
    projectId: string,
    file: File,
    payload: JobCreationInquiryPayload
  ): Promise<BaseResponse<JobCreationInquiryData>> => {
    try {
      const formData = new FormData();
      formData.append('projectId', projectId);
      formData.append('file', file);
      formData.append('payload', JSON.stringify(payload));

      const response = await axiosInstance.post(`/v1/legacy/cms/project/job-creation/inquiry`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const jobCreationExecute = async (
    token: string,
    inquiryId: string
  ): Promise<BaseResponse<JobCreationExecuteData>> => {
    try {
      const response = await axiosInstance.post(
        `/v1/legacy/cms/project/job-creation/execute`,
        { inquiryId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };


  const projectEnd = async (
    token: string,
    id: string
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/project/end`, { id }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const projectClusterConfig = async (
    token: string
  ): Promise<BaseResponse<ProjectClusterConfigResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/project/cluster-config`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  return {
    projectList,
    projectDetail,
    projectConfig,
    projectCreate,
    projectUpdate,
    projectDelete,
    projectEnd,
    uploadLogo,
    projectJobList,
    projectJobDetail,
    projectJobGeoList,
    projectJobGeoConfig,
    projectTaskList,
    projectTaskConfig,
    projectTaskAssign,
    projectTaskAssignByJob,
    downloadTemplateAdvanceAssignment,
    checkPreviewAssignment,
    projectJobExportXlsx,
    proceedJobAssignment,
    projectTaskConfigJob,
    projectTaskConfigFieldUser,
    projectJobUpdate,
    getJobCreationConfig,
    jobCreationInquiry,
    jobCreationExecute,
    projectClusterConfig,
    projectJobGroupConfig,
    projectJobChart
  };
};

export default ProjectService;

